/**
 * Employee detail — profile header + sections for reviews, recognition,
 * documents, time-off, and a merged activity timeline. Back button returns
 * to the employee list.
 *
 * Each section fetches data scoped to this employee. Server-side scoping still
 * applies: e.g. an Employee viewing their own profile only sees Public documents
 * and Shared reviews. Confidential-rank and status badges reflect that.
 *
 * Activity timeline fans out client-side over existing list APIs (no dedicated
 * backend timeline endpoint). Errors/empty sources are skipped quietly.
 */
import { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  Phone,
  Building2,
  Calendar,
  FileText,
  Star,
  CalendarClock,
  Pencil,
  Trash2,
  History,
  ShieldCheck,
  KeyRound,
} from 'lucide-react';
import {
  useEmployee,
  useDeleteEmployee,
} from '../api.js';
import { useAuthStore } from '../../../store/auth.js';
import { Button } from '../../../shell/Button.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { EmployeeFormModal } from '../components/EmployeeFormModal.js';
import { TerminationModal } from '../components/TerminationModal.js';
import { EmployeeAccountCard } from '../components/EmployeeAccountCard.js';
import { employeeLegalName } from '../lib/names.js';
import { useReviews } from '../../reviews/api.js';
import { useRecognition } from '../../recognition/api.js';
import { useDisciplinary } from '../../disciplinary/api.js';
import { useConversations } from '../../conversations/api.js';
import { useTimeOff } from '../../timeoff/api.js';
import { useOnboardingAssignments } from '../../onboarding/api.js';
import { useDocuments } from '../../documents/api.js';
import { useNaidDetail } from '../../naid/api.js';
import { EmployeeFileNotesSection } from '../../file-notes/components/EmployeeFileNotesSection.js';
import { Badge } from '../../../shell/Badge.js';
import { formatDate, formatDateTime } from '../../../lib/format.js';
import { PersonAvatar, employeeAvatarSrc } from '../../../shell/PersonAvatar.js';
import { AvatarUploadControl } from '../../me/components/AvatarUploadControl.js';
import { canEditOwnAvatar } from '../../me/avatar.js';
import { Table, type Column } from '../../../shell/Table.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import type {
  Employee,
  Review,
  Recognition,
  TimeOffRequest,
  Document,
  NaidComplianceStatus,
} from '@shared/types';

const statusTone = {
  employment: { Active: 'green', OnLeave: 'yellow', Terminated: 'red' } as Record<
    Employee['employmentStatus'],
    'green' | 'yellow' | 'red'
  >,
};

const naidTone: Record<NaidComplianceStatus, 'green' | 'yellow' | 'red' | 'slate' | 'blue'> = {
  Compliant: 'green',
  DueSoon: 'yellow',
  Overdue: 'red',
  Incomplete: 'blue',
  NotApplicable: 'slate',
};

const reviewStatusTone: Record<Review['status'], 'green' | 'yellow'> = {
  Shared: 'green',
  Draft: 'yellow',
};

const timeOffStatusTone: Record<TimeOffRequest['status'], 'yellow' | 'green' | 'red'> = {
  Pending: 'yellow',
  Approved: 'green',
  Denied: 'red',
};

const rankTone: Record<Document['confidentialRank'], 'green' | 'yellow' | 'red'> = {
  Public: 'green',
  Manager: 'yellow',
  Admin: 'red',
};

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [editOpen, setEditOpen] = useState(false);
  const [terminationOpen, setTerminationOpen] = useState(false);
  const { data: employee, isLoading } = useEmployee(id);
  const canViewNaid = user?.role === 'Admin' || user?.role === 'Manager';
  const canViewDocuments = user?.role === 'Admin' || user?.role === 'Manager';
  const { data: naidRow } = useNaidDetail(canViewNaid ? id : undefined);
  const { data: reviewsData } = useReviews({ employeeId: id });
  const { data: recognitionData } = useRecognition({ employeeId: id });
  const { data: timeOffData } = useTimeOff({ employeeId: id });
  const { data: documentsData } = useDocuments({ employeeId: id, enabled: canViewDocuments });

  const deleteEmployee = useDeleteEmployee();
  const handleDelete = async () => {
    if (!employee) return;
    if (!window.confirm(`Delete employee ${employee.name}? This cannot be undone.`)) return;
    try {
      await deleteEmployee.mutateAsync(employee.id);
      toast('success', 'Employee deleted.');
      navigate('/employees');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete employee.';
      toast('error', msg);
    }
  };

  const isAdmin = user?.role === 'Admin';

  if (isLoading || !employee) {
    return <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>;
  }

  const avatarSrc = employeeAvatarSrc(employee.id, employee.avatarStorageKey);
  const canEditAvatar = canEditOwnAvatar(employee.id, user?.employeeId);

  const reviewCols: Column<Review>[] = [
    { key: 'type', header: 'Type', render: (r) => r.type },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge tone={reviewStatusTone[r.status]}>{r.status}</Badge>,
    },
    {
      key: 'visible',
      header: 'Shared On',
      render: (r) => (r.visibleToEmployeeAt ? formatDate(r.visibleToEmployeeAt) : '—'),
    },
  ];

  const timeOffCols: Column<TimeOffRequest>[] = [
    { key: 'type', header: 'Type', render: (t) => t.type },
    { key: 'from', header: 'From', render: (t) => formatDate(t.from) },
    { key: 'to', header: 'To', render: (t) => formatDate(t.to) },
    {
      key: 'duration',
      header: 'Duration',
      render: (t) => (
        t.duration === 'PartialDay' && t.startTime && t.endTime
          ? `${t.startTime}–${t.endTime}`
          : 'Full day'
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (t) => (
        <span className="block max-w-[12rem] truncate text-sm text-slate-600 dark:text-slate-400" title={t.reason ?? undefined}>
          {t.reason ?? '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <Badge tone={timeOffStatusTone[t.status]}>{t.status}</Badge>,
    },
  ];

  const documentCols: Column<Document>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (d) => <a href={`/api/documents/${d.id}/content`} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 underline dark:text-slate-100">{d.name}</a>,
    },
    { key: 'category', header: 'Category', render: (d) => <Badge tone="blue">{d.category}</Badge> },
    {
      key: 'rank',
      header: 'Confidential',
      render: (d) => <Badge tone={rankTone[d.confidentialRank]}>{d.confidentialRank}</Badge>,
    },
    { key: 'uploaded', header: 'Uploaded', render: (d) => formatDateTime(d.uploadedAt) },
  ];

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/employees')}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Employees
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {canEditAvatar ? (
            <AvatarUploadControl
              name={employee.name}
              src={avatarSrc}
              variant="profile"
              tone="inverse"
            />
          ) : (
            <PersonAvatar
              name={employee.name}
              src={avatarSrc}
              size="lg"
              tone="inverse"
            />
          )}
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">{employee.name}</h1>
            {employeeLegalName(employee) && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Legal name: {employeeLegalName(employee)}
              </p>
            )}
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {employee.title} · {employee.department}
            </p>
            <div className="mt-1">
              <Badge tone={statusTone.employment[employee.employmentStatus]}>
                {employee.employmentStatus}
              </Badge>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {employee.employmentStatus !== 'Terminated' && employee.id !== user?.employeeId && (
              <Button variant="danger" onClick={() => setTerminationOpen(true)}>Terminate employment</Button>
            )}
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteEmployee.isPending}>
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          </div>
        )}
      </div>

      {/* Contact info */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <InfoCard
          icon={<Building2 className="h-4 w-4" />}
          label="Department"
          value={employee.department}
        />
        <InfoCard
          icon={<Calendar className="h-4 w-4" />}
          label="Hire Date"
          value={formatDate(employee.hireDate)}
        />
        {employee.employeeNumber && (
          <InfoCard
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Employee #"
            value={employee.employeeNumber}
          />
        )}
        <InfoCard
          icon={<Building2 className="h-4 w-4" />}
          label="Work location"
          value={employee.workLocation === 'Remote' ? 'Remote' : 'On-site'}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
            <Mail className="h-4 w-4" /> Emails
          </div>
          <ul className="space-y-1.5">
            {(employee.contacts?.filter((c) => c.kind === 'email').length
              ? employee.contacts.filter((c) => c.kind === 'email')
              : [{ id: 'e', kind: 'email' as const, label: 'Work', value: employee.email, isPrimary: true, sortOrder: 0 }]
            ).map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
                <a
                  href={`mailto:${c.value}`}
                  className="truncate text-slate-800 hover:underline dark:text-slate-200"
                >
                  {c.value}
                </a>
                <span className="shrink-0 text-xs text-slate-400">
                  {c.label}{c.isPrimary ? ' · Primary' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
            <Phone className="h-4 w-4" /> Phones
          </div>
          {(employee.contacts?.filter((c) => c.kind === 'phone').length ?? 0) > 0 || employee.phone ? (
            <ul className="space-y-1.5">
              {(employee.contacts?.filter((c) => c.kind === 'phone').length
                ? employee.contacts.filter((c) => c.kind === 'phone')
                : employee.phone
                  ? [{ id: 'p', kind: 'phone' as const, label: 'Work', value: employee.phone, isPrimary: true, sortOrder: 0 }]
                  : []
              ).map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
                  <a
                    href={`tel:${c.value}`}
                    className="truncate text-slate-800 hover:underline dark:text-slate-200"
                  >
                    {c.value}
                  </a>
                  <span className="shrink-0 text-xs text-slate-400">
                    {c.label}{c.isPrimary ? ' · Primary' : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">No phone numbers</p>
          )}
        </div>
      </div>

      {isAdmin && <EmployeeAccountCard employee={employee} />}

      {isAdmin && (
        <div className="rounded-lg border border-red-200 bg-red-50/30 p-4 dark:border-red-900/40 dark:bg-red-950/20">
          <EmployeeFileNotesSection employeeId={employee.id} />
        </div>
      )}

      {canViewNaid && naidRow && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <ShieldCheck className="h-4 w-4 text-slate-500" />
              NAID &amp; Key Fob
            </h2>
            <Link
              to="/naid"
              className="text-sm text-sky-600 hover:underline dark:text-sky-400"
            >
              Open NAID
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={naidTone[naidRow.naid?.complianceStatus ?? 'Incomplete']}>
              {naidRow.naid?.complianceStatus ?? 'Incomplete'}
            </Badge>
            {naidRow.keyFob ? (
              <span className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300">
                <KeyRound className="h-4 w-4" />
                Fob {naidRow.keyFob.serialNumber}
              </span>
            ) : naidRow.missingKeyFob ? (
              <Badge tone="yellow">Key Fob missing</Badge>
            ) : null}
            <span className="text-sm text-slate-500">
              Background due {formatDate(naidRow.naid?.backgroundDue)}
              {naidRow.naid?.accessRequired
                ? ` · Yearly due ${formatDate(naidRow.naid.yearlyTestDue)}`
                : ''}
            </span>
          </div>
        </div>
      )}

      {/* Reviews */}
      <Section
        icon={<Star className="h-4 w-4" />}
        title="Reviews"
        count={reviewsData?.items.length}
      >
        <Table columns={reviewCols} data={reviewsData?.items ?? []} />
      </Section>

      {/* Recognition */}
      <Section
        icon={<Star className="h-4 w-4" />}
        title="Recognition"
        count={recognitionData?.items.length}
      >
        {(recognitionData?.items.length ?? 0) > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(recognitionData?.items ?? []).map((r: Recognition) => (
              <div
                key={r.id}
                className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-center justify-between">
                  <Badge tone="purple">{r.badge}</Badge>
                  {!r.public && <Badge tone="slate">Private</Badge>}
                </div>
                <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{r.text}</p>
                <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">{formatDate(r.date)}</p>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="No recognition" description="Recognition for this employee will appear here." />
        )}
      </Section>

      {canViewDocuments && (
        <Section
          icon={<FileText className="h-4 w-4" />}
          title="Documents"
          count={documentsData?.items.length}
        >
          <Table columns={documentCols} data={documentsData?.items ?? []} />
        </Section>
      )}

      {/* Time Off */}
      <Section
        icon={<CalendarClock className="h-4 w-4" />}
        title="Time Off"
        count={timeOffData?.items.length}
      >
        <Table columns={timeOffCols} data={timeOffData?.items ?? []} />
      </Section>

      {/* Activity timeline — merged from domain list APIs (owned file only) */}
      <Section icon={<History className="h-4 w-4" />} title="Activity">
        <EmployeeActivityTimeline employeeId={employee.id} />
      </Section>

      {isAdmin && (
        <EmployeeFormModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          employee={employee}
        />
      )}
      {isAdmin && terminationOpen && (
        <TerminationModal employee={employee} signerName={user?.name || ''} onClose={() => setTerminationOpen(false)} />
      )}
    </div>
  );
}

function InfoCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-1.5 text-slate-400 dark:text-slate-500">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className="mt-1 truncate text-sm font-medium text-slate-900 dark:text-slate-100">{value}</p>
    </div>
  );
}

function Section({
  icon,
  title,
  count,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-slate-400 dark:text-slate-500">{icon}</span>
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        {count !== undefined && (
          <span className="text-xs text-slate-400 dark:text-slate-500">({count})</span>
        )}
      </div>
      {children}
    </div>
  );
}

/* ─── Activity timeline (inlined; ownership: EmployeeDetailPage only) ─── */

type ActivityKind =
  | 'review'
  | 'recognition'
  | 'disciplinary'
  | 'conversation'
  | 'time_off'
  | 'onboarding';

type BadgeTone = 'slate' | 'green' | 'blue' | 'yellow' | 'red' | 'purple';

interface ActivityItem {
  id: string;
  kind: ActivityKind;
  date: string;
  title: string;
  summary: string;
  href?: string;
}

const KIND_LABEL: Record<ActivityKind, string> = {
  review: 'Review',
  recognition: 'Recognition',
  disciplinary: 'Disciplinary',
  conversation: 'Conversation',
  time_off: 'Time off',
  onboarding: 'Onboarding',
};

const KIND_TONE: Record<ActivityKind, BadgeTone> = {
  review: 'purple',
  recognition: 'purple',
  disciplinary: 'red',
  conversation: 'blue',
  time_off: 'yellow',
  onboarding: 'blue',
};

function truncate(text: string, max = 100): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function activitySortKey(date: string): number {
  if (!date) return 0;
  const t = Date.parse(date);
  return Number.isFinite(t) ? t : 0;
}

function EmployeeActivityTimeline({ employeeId }: { employeeId: string }) {
  const { user } = useAuthStore();
  const canViewBoarding = user?.role === 'Admin' || user?.role === 'Manager';
  const reviews = useReviews({ employeeId });
  const recognition = useRecognition({ employeeId });
  const disciplinary = useDisciplinary({ employeeId });
  const conversations = useConversations({ employeeId });
  const timeOff = useTimeOff({ employeeId });
  const onboarding = useOnboardingAssignments({ employeeId, enabled: canViewBoarding });


  const items = useMemo(() => {
    const next: ActivityItem[] = [];

    for (const r of reviews.data?.items ?? []) {
      next.push({
        id: `review-${r.id}`,
        kind: 'review',
        date: r.visibleToEmployeeAt ?? '',
        title: `${r.type} review`,
        summary: r.status === 'Shared' ? 'Shared with employee' : 'Draft',
        href: `/reviews/${r.id}`,
      });
    }

    for (const r of recognition.data?.items ?? []) {
      next.push({
        id: `recognition-${r.id}`,
        kind: 'recognition',
        date: r.date,
        title: `${r.badge} recognition`,
        summary: truncate(r.text),
        href: '/recognition',
      });
    }

    for (const d of disciplinary.data?.items ?? []) {
      next.push({
        id: `disciplinary-${d.id}`,
        kind: 'disciplinary',
        date: d.date,
        title: `${d.severity} action`,
        summary: truncate(d.details || d.actionPlan || 'Disciplinary action recorded'),
        href: '/disciplinary',
      });
    }

    for (const c of conversations.data?.items ?? []) {
      next.push({
        id: `conversation-${c.id}`,
        kind: 'conversation',
        date: c.date,
        title: c.topic || 'Documented conversation',
        summary: truncate(c.details || 'Conversation documented'),
        href: '/conversations',
      });
    }

    for (const t of timeOff.data?.items ?? []) {
      next.push({
        id: `timeoff-${t.id}`,
        kind: 'time_off',
        date: t.decidedAt ?? t.from,
        title: `${t.type} time off`,
        summary: `${formatDate(t.from)} → ${formatDate(t.to)} · ${t.status}${t.reason ? ` · ${t.reason}` : ''}`,
        href: '/time-off',
      });
    }

    for (const a of onboarding.data?.items ?? []) {
      const progress =
        a.totalCount != null
          ? `${a.completedCount ?? 0}/${a.totalCount} items · ${a.status}`
          : a.status;
      next.push({
        id: `onboarding-${a.id}`,
        kind: 'onboarding',
        date: a.completedAt ?? a.assignedAt,
        title: a.templateName || 'Onboarding assignment',
        summary: progress,
        href: '/onboarding',
      });
    }

    next.sort((a, b) => {
      const diff = activitySortKey(b.date) - activitySortKey(a.date);
      if (diff !== 0) return diff;
      return a.id.localeCompare(b.id);
    });

    return next;
  }, [
    reviews.data?.items,
    recognition.data?.items,
    disciplinary.data?.items,
    conversations.data?.items,
    timeOff.data?.items,
    onboarding.data?.items,
  ]);

  const stillLoading =
    (reviews.isLoading ||
      recognition.isLoading ||
      disciplinary.isLoading ||
      conversations.isLoading ||
      timeOff.isLoading ||
      onboarding.isLoading) &&
    items.length === 0;

  if (stillLoading) {
    return (
      <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
        Loading activity…
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title="No recent activity"
        description="Reviews, recognition, time off, and other events for this employee will show up here."
      />
    );
  }

  return (
    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
      {items.map((item) => {
        const body = (
          <>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={KIND_TONE[item.kind]}>{KIND_LABEL[item.kind]}</Badge>
                <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                  {item.title}
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {item.date ? formatDate(item.date) : '—'}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{item.summary}</p>
            </div>
            {item.href && <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />}
          </>
        );

        return (
          <li key={item.id}>
            {item.href ? (
              <Link
                to={item.href}
                className="flex items-center justify-between gap-3 px-3 py-3 transition hover:bg-slate-50 dark:hover:bg-slate-800/40"
              >
                {body}
              </Link>
            ) : (
              <div className="flex items-center justify-between gap-3 px-3 py-3">{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
